import { useState, useCallback, useEffect, useMemo } from 'react';
import { useApi } from '../../../../hooks/useApi';
import {
  loadPriceProducts, fetchPrices, updatePrices,
  getPromotionCatalog, syncPromotionState,
  setPromotionActionPrice, exitPromotion, removeFromAutoAdd,
} from '../../../../services/ozonPrices';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import { fmtDate, fmtDateTime } from '../../../../utils/ruDates';
import { matchesSearch } from '../../../../utils/search';

const PRICE_FIELDS = [
  { key: 'price', header: 'Предельная цена', hint: 'Основная цена товара (price)' },
  { key: 'old_price', header: 'Зачёркнутая цена', hint: 'Маркетинговая цена «до» (old_price)' },
  { key: 'min_price', header: 'Ограничение для акций и стратегий', hint: 'Ниже этой цены акции и стратегии не опускают цену (min_price)' },
];

const num = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const money = (v) => (v == null || v === '' ? '—' : `${Number(v).toLocaleString('ru-RU')} ₽`);

function PriceCell({ value, disabled, onCommit }) {
  const [draft, setDraft] = useState(null);
  const shown = draft ?? (value ?? '');
  const commit = () => {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    if (next !== '' && next !== String(value ?? '') && num(next) > 0) onCommit(next);
  };
  return (
    <input
      type="text" inputMode="decimal" style={{ width: 90, textAlign: 'right' }}
      value={shown} disabled={disabled}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') setDraft(null);
      }}
      onClick={e => e.stopPropagation()}
    />
  );
}

function JoinCell({ candidates, disabled, onJoin }) {
  const [actionId, setActionId] = useState('');
  const [price, setPrice] = useState('');
  if (!candidates.length) return <span>—</span>;
  const picked = candidates.find(c => String(c.action_id) === actionId);
  const pick = (id) => {
    setActionId(id);
    const c = candidates.find(x => String(x.action_id) === id);
    setPrice(c?.max_action_price != null ? String(c.max_action_price) : '');
  };
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
      <select value={actionId} onChange={e => pick(e.target.value)} style={{ maxWidth: 170 }}>
        <option value="">Акция ({candidates.length})</option>
        {candidates.map(c => (
          <option key={c.action_id} value={c.action_id}>
            {c.action_name || c.action_id}{c.max_action_price != null ? ` · до ${c.max_action_price} ₽` : ''}
          </option>
        ))}
      </select>
      <input type="text" inputMode="decimal" style={{ width: 70 }} placeholder="цена"
        value={price} disabled={!picked} onChange={e => setPrice(e.target.value)} />
      <button type="button" className="btn btn--secondary" disabled={disabled || !picked || !(num(price) > 0)}
        title={picked?.max_action_price != null
          ? `Лимит акции ${picked.max_action_price} ₽: цена не выше лимита`
          : 'Войти в акцию по этой цене'}
        onClick={() => { onJoin(picked, num(price)); setActionId(''); setPrice(''); }}>
        Войти
      </button>
    </div>
  );
}

function ParticipationCell({ items, disabled, onExit, onSetPrice }) {
  if (!items.length) return <span className="ozp__badge ozp__badge--red">Нет</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {items.map(p => {
        const title = [
          p.action_name || p.action_id,
          p.max_action_price != null ? `лимит акции ${p.max_action_price} ₽` : null,
          p.alert_max_action_price != null ? `рекомендуемая ${p.alert_max_action_price} ₽` : null,
          p.current_boost != null ? `бустинг ${p.current_boost}% (${p.min_boost ?? '?'}–${p.max_boost ?? '?'}%)` : null,
          p.stock != null ? `в акции ${p.stock} шт, минимум ${p.min_stock ?? '—'}` : null,
          p.is_quarantined ? 'товар в карантине' : null,
        ].filter(Boolean).join(' · ');
        return (
          <span key={p.action_id} className={`ozp__badge ozp__badge--${p.alert_max_action_price_failed || p.is_quarantined ? 'orange' : 'green'}`}
            title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 300 }}>
            <span style={{ maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.action_name || p.action_id}
            </span>
            <button type="button" disabled={disabled} title="Изменить акционную цену"
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, font: 'inherit', fontWeight: 600 }}
              onClick={e => {
                e.stopPropagation();
                const raw = window.prompt(`Акционная цена в «${p.action_name || p.action_id}»`, String(p.action_price ?? ''));
                if (raw != null && num(raw) > 0) onSetPrice(p, num(raw));
              }}>
              {money(p.action_price)}
            </button>
            <button type="button" disabled={disabled} title="Вывести из акции (цена станет равна лимиту акции)"
              style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontWeight: 700, lineHeight: 1 }}
              onClick={e => { e.stopPropagation(); onExit(p); }}>
              ×
            </button>
          </span>
        );
      })}
    </div>
  );
}

function AutoAddCell({ items, disabled, onRemove }) {
  if (!items.length) return <span>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {items.map(a => (
        <span key={`${a.action_id}-${a.auto_add_date}`}
          className={`ozp__badge ozp__badge--${a.will_be_quarantined || a.has_expired_min_seller_price ? 'orange' : 'yellow'}`}
          title={[
            a.action_name,
            a.quantity_to_auto_add != null ? `${a.quantity_to_auto_add} шт` : null,
            a.will_be_quarantined ? 'после автодобавления попадёт в карантин' : null,
            a.has_expired_min_seller_price ? 'ограничение для акций истекло' : null,
          ].filter(Boolean).join(' · ')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 300 }}>
          <span>{a.auto_add_date ? fmtDate(a.auto_add_date) : '—'}: {money(a.action_price_to_auto_add)}</span>
          <button type="button" disabled={disabled} title="Убрать из автодобавления на эту дату"
            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontWeight: 700, lineHeight: 1 }}
            onClick={e => { e.stopPropagation(); onRemove(a); }}>
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

function resultMessage(result, okCount) {
  const rejected = (result?.rejected || []).map(r => r.reason).filter(Boolean);
  return rejected.length ? `Ozon отклонил: ${rejected.join('; ')}` : `Готово: ${okCount}`;
}

export default function OzonPromoPrices({ accountId }) {
  const { request, showStatus } = useApi();
  const [products, setProducts] = useState([]);
  const [priceMap, setPriceMap] = useState({});
  const [catalog, setCatalog] = useState({});
  const [syncedAt, setSyncedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const reloadCatalog = useCallback(async () => {
    const data = await getPromotionCatalog({ request, accountId });
    const map = {};
    for (const it of data.items || []) map[String(it.product_id)] = it;
    setCatalog(map);
    setSyncedAt(data.synced_at || null);
  }, [request, accountId]);

  const reloadPrices = useCallback(async (items) => {
    const offerIds = items.map(p => p.offer_id).filter(Boolean);
    if (!offerIds.length) return;
    const priceItems = await fetchPrices({ request, accountId, offerIds });
    const map = {};
    for (const it of priceItems) map[it.offer_id] = it;
    setPriceMap(map);
  }, [request, accountId]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const items = await loadPriceProducts({ request, accountId });
      setProducts(items);
      await reloadCatalog();
      await reloadPrices(items);
    } catch (err) {
      showStatus('error', err?.message || 'Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  }, [accountId, request, reloadCatalog, reloadPrices, showStatus]);

  useEffect(() => { load(); }, [load]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await syncPromotionState({ request, accountId });
      await reloadCatalog();
      showStatus('success', `Акции обновлены: ${r.actions}, строк ${r.rows}${r.failed_actions ? `, не прочитано акций: ${r.failed_actions}` : ''}`);
    } catch (err) {
      showStatus('error', err?.message || 'Не удалось обновить акции из Ozon');
    } finally {
      setSyncing(false);
    }
  }, [request, accountId, reloadCatalog, showStatus]);

  const run = useCallback(async (key, fn, onDone) => {
    setBusyKey(key);
    try {
      await fn();
      await onDone?.();
    } catch (err) {
      showStatus('error', err?.message || 'Операция не выполнена');
    } finally {
      setBusyKey(null);
    }
  }, [showStatus]);

  const handlePriceCommit = useCallback((row, field, value) => run(row.offer_id, async () => {
    const pr = priceMap[row.offer_id] || {};
    const next = { price: pr.price, old_price: pr.old_price, min_price: pr.min_price, [field]: value };
    if (!next.price) throw new Error('Нет предельной цены товара — сначала загрузите цены');
    const result = await updatePrices({ request, accountId, prices: [{ offer_id: row.offer_id, ...next }] });
    const r = (result?.result || [])[0];
    if (!r?.updated) throw new Error((r?.errors || []).map(e => e.message || String(e)).join('; ') || 'Ozon не принял цену');
    setPriceMap(prev => ({ ...prev, [row.offer_id]: { ...prev[row.offer_id], [field]: String(value) } }));
    showStatus('success', 'Цена обновлена');
  }), [run, priceMap, request, accountId, showStatus]);

  const handleExit = useCallback((row, p) => {
    if (!window.confirm(`Вывести ${row.offer_id} из акции «${p.action_name || p.action_id}»? Акционная цена станет равна лимиту акции.`)) return;
    return run(`${row.offer_id}-x-${p.action_id}`, async () => {
      const r = await exitPromotion({ request, accountId, actionId: p.action_id, productIds: [String(row.product_id)] });
      showStatus((r.rejected || []).length ? 'error' : 'success', resultMessage(r, (r.deactivated_product_ids || []).length));
    }, reloadCatalog);
  }, [run, request, accountId, reloadCatalog, showStatus]);

  const handleSetActionPrice = useCallback((row, actionId, price) => run(`${row.offer_id}-p-${actionId}`, async () => {
    const r = await setPromotionActionPrice({
      request, accountId, actionId, items: [{ product_id: String(row.product_id), action_price: price }],
    });
    showStatus((r.rejected || []).length ? 'error' : 'success', resultMessage(r, (r.active_product_ids || []).length));
  }, reloadCatalog), [run, request, accountId, reloadCatalog, showStatus]);

  const handleRemoveAutoAdd = useCallback((row, a) => {
    if (!window.confirm(`Убрать ${row.offer_id} из автодобавления в «${a.action_name || a.action_id}» на ${fmtDateTime(a.auto_add_date)}?`)) return;
    return run(`${row.offer_id}-a-${a.action_id}`, async () => {
      await removeFromAutoAdd({
        request, accountId, actionId: a.action_id, autoAddDate: a.auto_add_date, productIds: [String(row.product_id)],
      });
      showStatus('success', 'Убрано из автодобавления');
    }, reloadCatalog);
  }, [run, request, accountId, reloadCatalog, showStatus]);

  const rows = useMemo(() => products.map(p => {
    const pr = priceMap[p.offer_id] || {};
    const c = catalog[String(p.product_id)] || {};
    return {
      offer_id: p.offer_id, product_id: p.product_id, name: p.name, sku: p.sku,
      stock: pr.stock ?? null,
      price: pr.price || '', old_price: pr.old_price || '', min_price: pr.min_price || '',
      marketing_seller_price: pr.marketing_seller_price || '',
      participations: c.participations || [], candidates: c.candidates || [],
      auto_add: c.auto_add || [], auto_add_candidates: c.auto_add_candidates || [],
    };
  }), [products, priceMap, catalog]);

  const visible = useMemo(() => rows.filter(r => {
    if (filter === 'in_promo' && !r.participations.length) return false;
    if (filter === 'auto_add' && !r.auto_add.length) return false;
    if (filter === 'can_join' && !r.candidates.length) return false;
    return matchesSearch(r, search, ['offer_id', 'name', 'sku']);
  }), [rows, filter, search]);

  const columns = useMemo(() => [
    { key: 'offer_id', header: 'Артикул', width: 140, type: 'text', sticky: true },
    {
      key: 'name', header: 'Название', width: 280, type: 'custom',
      render: value => (
        <span title={value || ''} style={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3,
        }}>{value || '—'}</span>
      ),
    },
    ...PRICE_FIELDS.map(f => ({
      key: f.key, header: f.header, width: f.key === 'min_price' ? 150 : 120, type: 'custom', align: 'right', sortable: false,
      render: (value, row) => (
        <span title={f.hint}>
          <PriceCell value={value} disabled={busyKey === row.offer_id || loading}
            onCommit={v => handlePriceCommit(row, f.key, v)} />
        </span>
      ),
    })),
    {
      key: 'marketing_seller_price', header: 'Цена с акциями продавца', width: 130, type: 'custom', align: 'right', sortable: false,
      render: value => <span title="Цена с учётом акций, кроме акций за счёт Ozon">{money(num(value))}</span>,
    },
    { key: 'stock', header: 'Остаток', width: 80, type: 'number', align: 'right' },
    {
      key: 'participations', header: 'Участие в акциях', width: 320, type: 'custom', sortable: false,
      render: (value, row) => (
        <ParticipationCell items={value} disabled={!!busyKey}
          onExit={p => handleExit(row, p)}
          onSetPrice={(p, price) => handleSetActionPrice(row, p.action_id, price)} />
      ),
    },
    {
      key: 'candidates', header: 'Войти в акцию', width: 360, type: 'custom', sortable: false,
      render: (value, row) => (
        <JoinCell candidates={value} disabled={!!busyKey}
          onJoin={(c, price) => handleSetActionPrice(row, c.action_id, price)} />
      ),
    },
    {
      key: 'auto_add', header: 'Автодобавление', width: 260, type: 'custom', sortable: false,
      render: (value, row) => (
        <AutoAddCell items={value} disabled={!!busyKey} onRemove={a => handleRemoveAutoAdd(row, a)} />
      ),
    },
  ], [busyKey, loading, handlePriceCommit, handleExit, handleSetActionPrice, handleRemoveAutoAdd]);

  const inPromoCount = rows.filter(r => r.participations.length).length;
  const autoAddCount = rows.filter(r => r.auto_add.length).length;

  return (
    <div className="ozp__section">
      <div className="ozp__toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
        <button className="btn btn--primary" onClick={handleSync} disabled={syncing || loading}>
          {syncing ? 'Опрашиваем Ozon…' : 'Обновить акции из Ozon'}
        </button>
        <button className="btn btn--secondary" onClick={load} disabled={loading || syncing}>
          {loading ? 'Загрузка…' : 'Обновить цены'}
        </button>
        <select value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="all">Все товары</option>
          <option value="in_promo">Участвуют в акциях</option>
          <option value="can_join">Можно войти в акцию</option>
          <option value="auto_add">В автодобавлении</option>
        </select>
        <span className="ozp__count">
          {inPromoCount} в акциях · {autoAddCount} в автодобавлении · из {rows.length} товаров
        </span>
        <span className="ozp__count">
          Акции из Ozon: {syncedAt ? fmtDateTime(syncedAt) : 'ещё не загружались'}
        </span>
      </div>
      <div className="ozp__soon" style={{ margin: '8px 0 12px' }}>
        Цены меняются сразу после ввода значения и Enter. Участие в акции определяется акционной ценой:
        «×» выводит товар из акции, ставя цену, равную лимиту акции. Все ручные изменения попадают в журнал цен.
      </div>
      {!syncedAt && !loading && (
        <div className="empty" style={{ margin: '24px 0' }}>
          Участие в акциях ещё не загружено. Нажмите «Обновить акции из Ozon».
        </div>
      )}
      <UnifiedTable
        tableId="ozon-promo-prices"
        columns={columns}
        data={visible}
        getRowKey={row => row.offer_id}
        search={search}
        onSearchChange={setSearch}
        exportFilename="ozon-promo-prices"
        loading={loading}
      />
    </div>
  );
}
